import { useEffect, useState } from "react";
import clsx from "clsx";
import { useWorldBossStore, formatBossWindowLabel } from "../store/useWorldBossStore";
import { useGameStore } from "../store/useGameStore";
import { FormattedNumber } from "../components/FormattedNumber";

const TABS = [
  { id: "ALL" as const, label: "All" },
  { id: "WARRIOR" as const, label: "Warrior" },
  { id: "ARCHER" as const, label: "Archer" },
  { id: "MAGE" as const, label: "Mage" },
];

export function WorldBossScreen() {
  const { hero, balance, setScreen } = useGameStore();
  const {
    status,
    leaderboard,
    tab,
    loading,
    attacking,
    error,
    pollStatus,
    setTab,
    refreshLeaderboard,
    heartbeat,
    attack,
  } = useWorldBossStore();

  const [deathCooldownLeft, setDeathCooldownLeft] = useState(0);

  useEffect(() => {
    void useGameStore.getState().refreshBalance();
    void pollStatus().then(() => {
      void refreshLeaderboard();
    });
    const timer = setInterval(() => {
      void pollStatus();
      void refreshLeaderboard();
      void heartbeat();
    }, 5000);
    return () => clearInterval(timer);
  }, [pollStatus, refreshLeaderboard, heartbeat]);

  useEffect(() => {
    if (status?.is_active) void heartbeat();
  }, [status?.is_active, heartbeat]);

  useEffect(() => {
    setDeathCooldownLeft(status?.death_cooldown_seconds ?? 0);
  }, [status?.death_cooldown_seconds]);

  useEffect(() => {
    if (deathCooldownLeft <= 0) return;
    const tick = setInterval(() => {
      setDeathCooldownLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(tick);
  }, [deathCooldownLeft > 0]);

  const active = status?.is_active ?? false;
  const onDeathCooldown = deathCooldownLeft > 0;
  const reviveCost = status?.instant_revive_diamond_cost ?? 5;
  const diamonds = balance?.diamonds ?? 0;
  const canAffordRevive = diamonds >= reviveCost;
  const canAttack =
    active && !status?.boss_dead && !attacking && !onDeathCooldown;
  const canInstantRevive =
    active && !status?.boss_dead && !attacking && onDeathCooldown && canAffordRevive;
  const hpPct =
    status && status.boss_max_hp > 0
      ? Math.round((status.boss_hp / status.boss_max_hp) * 100)
      : 0;

  return (
    <div className="p-4 space-y-4 pb-8">
      <button type="button" onClick={() => setScreen("main")} className="text-sm text-gray-400 hover:text-white">
        ← Back to Home
      </button>

      <div className="text-center space-y-1">
        <h2 className="text-xl font-bold font-game text-gold-400">🐲 World Boss</h2>
        <p className="text-gray-400 text-sm">{status ? formatBossWindowLabel(status) : "Loading..."}</p>
      </div>

      {!active && status && (
        <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 text-center space-y-2">
          <p className="text-gray-300 text-sm">The World Boss is not active right now.</p>
          {status.seconds_until_start != null && (
            <p className="text-amber-300 text-sm">
              Opens in {formatDuration(status.seconds_until_start)}
            </p>
          )}
        </div>
      )}

      {active && status && (
        <>
          {onDeathCooldown && (
            <div className="bg-rose-950/50 border border-rose-500/60 rounded-xl p-4 text-center space-y-2">
              <p className="text-rose-200 font-semibold">You died</p>
              <p className="text-sm text-gray-300">
                Respawn in{" "}
                <span className="text-amber-300 font-bold tabular-nums">{deathCooldownLeft}s</span>
              </p>
            </div>
          )}

          <div className="bg-kingdom-purple border border-rose-500/40 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-white text-lg">{status.boss_name}</div>
                <div className="text-xs text-gray-400">Lv.{status.boss_level} · Shared server HP</div>
              </div>
              {status.seconds_remaining != null && (
                <div className="text-right">
                  <div className="text-[10px] text-gray-500 uppercase">Time left</div>
                  <div className="text-sm text-amber-300 font-semibold tabular-nums">
                    {formatDuration(status.seconds_remaining)}
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Boss HP</span>
                <span>
                  <FormattedNumber value={status.boss_hp} /> / <FormattedNumber value={status.boss_max_hp} />
                </span>
              </div>
              <div className="h-4 bg-kingdom-dark rounded-full overflow-hidden border border-rose-900/50">
                <div
                  className={clsx(
                    "h-full transition-all duration-500 rounded-full",
                    status.boss_dead ? "bg-gray-600 w-0" : "bg-gradient-to-r from-rose-600 to-red-500",
                  )}
                  style={{ width: status.boss_dead ? "0%" : `${hpPct}%` }}
                />
              </div>
            </div>

            <div className="flex justify-between text-sm">
              <span className="text-gray-400">
                Your damage: <span className="text-gold-300 font-semibold"><FormattedNumber value={status.my_total_damage} /></span>
              </span>
              <span className="text-gray-400">
                Rank: <span className="text-white font-semibold">{status.my_rank ? `#${status.my_rank}` : "—"}</span>
              </span>
            </div>

            {onDeathCooldown ? (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => void attack(true)}
                  disabled={!canInstantRevive}
                  className={clsx(
                    "w-full py-3 rounded-xl font-bold active:scale-95 transition-all",
                    canInstantRevive
                      ? "bg-gradient-to-r from-violet-600 to-indigo-700 text-white shadow-lg"
                      : "bg-kingdom-mid text-gray-500 cursor-not-allowed",
                  )}
                >
                  {attacking
                    ? "Reviving..."
                    : `Revive instantly · 💎 ${reviveCost}`}
                </button>
                {!canAffordRevive && (
                  <p className="text-[11px] text-rose-300 text-center">
                    Need 💎 {reviveCost} ({diamonds} available)
                  </p>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => void attack()}
                disabled={!canAttack}
                className={clsx(
                  "w-full py-3 rounded-xl font-bold active:scale-95 transition-all",
                  canAttack
                    ? "bg-gradient-to-r from-rose-600 to-red-700 text-white shadow-lg"
                    : "bg-kingdom-mid text-gray-500 cursor-not-allowed",
                )}
              >
                {status.boss_dead
                  ? "Boss defeated"
                  : attacking
                    ? "Fighting..."
                    : "Attack"}
              </button>
            )}

            <p className="text-[11px] text-gray-500 text-center leading-relaxed">
              Earn gold & arcane dust per hit. 💎 Diamonds (50 per 10% boss HP) are paid when the boss dies.
            </p>
          </div>
        </>
      )}

      {error && (
        <div className="bg-rose-900/30 border border-rose-500 rounded-xl p-3 text-rose-300 text-sm">{error}</div>
      )}

      {active && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">
            Damage leaderboard
            {status?.boss_dead && (
              <span className="normal-case font-normal text-gray-500 ml-1">· until event ends</span>
            )}
          </h3>
          <div className="flex gap-1 flex-wrap">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={clsx(
                  "px-3 py-1 rounded-lg text-xs font-semibold transition-colors",
                  tab === t.id
                    ? "bg-kingdom-glow/25 text-kingdom-glow border border-kingdom-glow/50"
                    : "bg-kingdom-mid text-gray-400 border border-transparent",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading && !leaderboard && (
            <p className="text-gray-500 text-sm text-center py-6">Loading...</p>
          )}

          {leaderboard && leaderboard.entries.length === 0 && (
            <p className="text-gray-500 text-sm text-center py-6">No damage recorded yet</p>
          )}

          {leaderboard && leaderboard.entries.length > 0 && (
            <div className="space-y-1">
              {leaderboard.entries.map((e) => (
                <div
                  key={`${e.user_id}-${e.rank}`}
                  className={clsx(
                    "flex items-center gap-3 rounded-lg px-3 py-2 border",
                    e.user_id === hero?.user_id
                      ? "bg-kingdom-glow/10 border-kingdom-glow/40"
                      : "bg-kingdom-purple/50 border-kingdom-mid/50",
                  )}
                >
                  <span className="text-gold-400 font-bold w-8 text-right">#{e.rank}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white truncate">{e.display_name}</div>
                    <div className="text-[10px] text-gray-500">{e.hero_class}</div>
                  </div>
                  <span className="text-sm text-gold-300 font-semibold tabular-nums">
                    <FormattedNumber value={e.damage} />
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}h ${rm}m`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
}
